import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  PortalLoadingCard,
  PortalMetricCard,
  PortalStateCard,
  formatPortalDate
} from './portalShared'
import SubcontractorSiteDetailDrawer from './SubcontractorSiteDetailDrawer'
import SubcontractorSprayZonesDialog from './SubcontractorSprayZonesDialog'

const DEFAULT_CENTER = [33.55, -112.0]

const URGENCY_COLORS = {
  overdue: 'var(--df-danger-text, #a9341d)',
  due_soon: 'var(--df-accent, #c66a45)',
  needs_completion: 'var(--df-warn-text, #8c5b00)',
  ok: '#9bb88f',
  never_done: 'var(--df-ink-4, #97a094)',
  ignored: 'var(--df-ink-4, #97a094)'
}

const useStyles = makeStyles((theme) => ({
  filterBar: {
    background: 'var(--df-surface)',
    border: '1px solid var(--df-line)',
    borderRadius: 12,
    padding: 14,
    marginBottom: 14,
    display: 'flex',
    alignItems: 'flex-end',
    gap: 12,
    flexWrap: 'wrap'
  },
  mapCard: {
    borderRadius: 'var(--df-radius-md)',
    overflow: 'hidden',
    boxShadow: 'var(--df-shadow-sm)',
    border: '1px solid var(--df-line)'
  },
  mapContainer: {
    width: '100%',
    minHeight: 680,
    height: 680,
    backgroundColor: 'var(--df-bg)'
  },
  selectAllRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 14px',
    fontSize: 12,
    color: 'var(--df-ink-3)',
    marginBottom: 10
  },
  siteCard: {
    position: 'relative',
    borderRadius: 12,
    border: '1px solid var(--df-line)',
    background: 'var(--df-surface)',
    cursor: 'pointer',
    transition: 'border-color .12s, box-shadow .12s',
    height: '100%',
    '&:hover': {
      borderColor: 'var(--df-line-2)',
      boxShadow: '0 4px 14px rgba(28,36,25,.08)'
    }
  },
  siteCardSelected: {
    borderColor: 'var(--df-primary) !important',
    boxShadow: '0 0 0 3px rgba(31,94,58,.14) !important'
  },
  urgencyRule: {
    position: 'absolute',
    left: 0,
    top: 14,
    bottom: 14,
    width: 3,
    borderRadius: 2
  },
  cardTop: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10
  },
  cardTitleBlock: {
    flex: 1,
    minWidth: 0
  },
  siteName: {
    fontSize: 14.5,
    fontWeight: 700,
    color: 'var(--df-ink)',
    lineHeight: 1.25,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  },
  siteAddress: {
    fontFamily: 'var(--df-font-mono)',
    fontSize: 11,
    color: 'var(--df-ink-3)',
    marginTop: 3,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  mapPin: {
    width: 22,
    height: 22,
    borderRadius: 6,
    background: 'var(--df-surface-2)',
    border: '1px solid var(--df-line)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--df-ink-3)',
    fontSize: 11,
    flexShrink: 0,
    marginTop: 1
  },
  mapPinUnmapped: {
    color: 'var(--df-warn-text, #8c5b00)',
    background: 'var(--df-warn-bg, #fff3d9)',
    borderColor: '#d9b97a'
  },
  chipRow: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    marginTop: 10
  },
  cardMeta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 10,
    borderTop: '1px dashed var(--df-line)',
    fontSize: 12,
    color: 'var(--df-ink-3)',
    marginTop: 10
  },
  metaLabel: {
    fontFamily: 'var(--df-font-mono)',
    fontSize: 10,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--df-ink-4)',
    marginRight: 6
  },
  metaValue: {
    fontFamily: 'var(--df-font-mono)',
    fontSize: 12,
    color: 'var(--df-ink-2)',
    fontVariantNumeric: 'tabular-nums'
  },
  metaValueDim: {
    fontFamily: 'var(--df-font-mono)',
    fontSize: 12,
    color: 'var(--df-ink-4)',
    fontVariantNumeric: 'tabular-nums'
  },
  cardPrompt: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--df-primary)',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:hover': { textDecoration: 'underline' }
  },
  bulkBar: {
    position: 'fixed',
    left: '50%',
    bottom: 24,
    transform: 'translateX(-50%)',
    background: 'var(--df-ink)',
    color: '#f4efe4',
    borderRadius: 14,
    padding: '10px 14px 10px 18px',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    boxShadow: '0 18px 48px rgba(28,36,25,.32), 0 0 0 1px rgba(255,255,255,.06) inset',
    zIndex: 50
  },
  bulkCount: {
    fontFamily: 'var(--df-font-mono)',
    fontSize: 13,
    color: '#fff',
    fontWeight: 600
  },
  bulkLabel: {
    fontSize: 13,
    color: '#c8d0c4'
  },
  bulkSep: {
    width: 1,
    height: 22,
    background: 'rgba(255,255,255,.12)'
  }
}))

const assignmentViewOptions = [
  { value: 'all', label: 'All visible sites' },
  { value: 'assigned', label: 'Assigned only' },
  { value: 'unassigned', label: 'Unassigned only' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'unmapped', label: 'Unmapped' }
]

const sortOptions = [
  { value: 'nextScheduledDate', label: 'Urgency · due date' },
  { value: 'lastAppliedDate', label: 'Last applied' },
  { value: 'siteName', label: 'Property name' },
  { value: 'address', label: 'Address (A-Z)' }
]

const EMPTY_OWNED_JOBSITE_FORM = {
  siteName: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  latitude: '',
  longitude: '',
  notes: ''
}

const EMPTY_OWNED_CONTACT_FORM = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  notes: ''
}

function hydrateOwnedJobsiteForm (jobsite) {
  return {
    siteName: jobsite?.siteName || '',
    address: jobsite?.address || '',
    city: jobsite?.city || '',
    state: jobsite?.state || '',
    zip: jobsite?.zip || '',
    latitude: jobsite?.latitude ?? '',
    longitude: jobsite?.longitude ?? '',
    notes: jobsite?.notes || ''
  }
}

function getUrgencyColor (status) {
  return URGENCY_COLORS[status] || URGENCY_COLORS.ok
}

function getUrgencyChip (item) {
  if (item.status === 'overdue') {
    const days = item.daysOverdue || item.daysLate || '?'
    return { label: `Overdue ${days}d`, className: 'df-chip-danger' }
  }
  if (item.status === 'due_soon') {
    const days = item.daysTilDue || '?'
    return { label: `Due in ${days}d`, className: 'df-chip-warn' }
  }
  if (item.status === 'needs_completion') {
    return { label: 'Needs completion', className: 'df-chip-warn' }
  }
  if (item.status === 'never_done') {
    return { label: 'Never applied', className: 'df-chip-warn' }
  }
  if (item.effectiveNextDate) {
    return { label: `Due ${formatPortalDate(item.effectiveNextDate, { fallback: '' })}`, className: '' }
  }
  return null
}

function getAssignmentChip (item) {
  if (item.isSubcontractorOwned && !item.scheduleItemId) {
    return { label: 'Subcontractor-owned', className: 'df-chip-info' }
  }
  if (item.assignedCrewName) {
    return { label: `Crew · ${item.assignedCrewName}`, className: 'df-chip-ok' }
  }
  return { label: 'Unassigned', className: 'df-chip-warn' }
}

function getCardPrompt (item, isAdmin) {
  if (item.isSubcontractorOwned && !item.scheduleItemId) return isAdmin ? 'Add to herbicide queue →' : 'Subcontractor-owned'
  if (item.isSubcontractorOwned) return isAdmin ? 'Manage spray zones →' : 'View herbicide job →'
  if (!item.lastAppliedDate && !item.effectiveNextDate) return 'Schedule first →'
  if (!item.assignedCrewName) return 'Assign crew →'
  if (item.status === 'overdue') return 'Reschedule →'
  return 'View site →'
}

function buildMarkerIcon (assigned = false) {
  const color = assigned ? '#1f5e3a' : '#8a692d'
  return L.divIcon({
    className: '',
    html: `<div style="width:16px;height:16px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 2px 10px rgba(0,0,0,0.25);"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8]
  })
}

const SubcontractorJobsites = ({ user, notification }) => {
  const classes = useStyles()
  const isAdmin = user?.subcontractorProfile?.role === 'admin'
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [items, setItems] = useState([])
  const [ownedJobsites, setOwnedJobsites] = useState([])
  const [summary, setSummary] = useState({})
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [assignmentView, setAssignmentView] = useState('all')
  const [sortBy, setSortBy] = useState('nextScheduledDate')
  const [viewMode, setViewMode] = useState('list')
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [selectedScheduleItemId, setSelectedScheduleItemId] = useState(null)
  const [crews, setCrews] = useState([])
  const [bulkCrewId, setBulkCrewId] = useState('')
  const [bulkAssigning, setBulkAssigning] = useState(false)
  const [ownedJobsiteDialogOpen, setOwnedJobsiteDialogOpen] = useState(false)
  const [editingOwnedJobsite, setEditingOwnedJobsite] = useState(null)
  const [ownedJobsiteForm, setOwnedJobsiteForm] = useState(EMPTY_OWNED_JOBSITE_FORM)
  const [ownedJobsiteSaving, setOwnedJobsiteSaving] = useState(false)
  const [ownedJobsiteError, setOwnedJobsiteError] = useState('')
  const [queueingOwnedJobsiteId, setQueueingOwnedJobsiteId] = useState(null)
  const [ownedJobsiteContacts, setOwnedJobsiteContacts] = useState([])
  const [ownedJobsiteContactsLoading, setOwnedJobsiteContactsLoading] = useState(false)
  const [ownedContactForm, setOwnedContactForm] = useState(EMPTY_OWNED_CONTACT_FORM)
  const [ownedContactSaving, setOwnedContactSaving] = useState(false)
  const [ownedContactError, setOwnedContactError] = useState('')
  const [sprayZonesJobsite, setSprayZonesJobsite] = useState(null)
  const mapRef = useRef(null)
  const mapContainerRef = useRef(null)
  const markersRef = useRef([])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearch(searchInput.trim())
    }, 250)
    return () => window.clearTimeout(timeoutId)
  }, [searchInput])

  const loadJobsites = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [response, ownedJobsitesResponse] = await Promise.all([
        axios.get('/api/subcontractor/jobsites', {
          params: { search, sortBy, sortOrder: 'asc', assignmentView: isAdmin ? assignmentView : 'assigned' }
        }),
        axios.get('/api/subcontractor/owned-jobsites', { params: { search } })
      ])
      setItems(response.data?.items || [])
      setSummary(response.data?.summary || {})
      setOwnedJobsites(ownedJobsitesResponse.data?.items || [])
    } catch (requestError) {
      console.error('Failed to load subcontractor jobsites', requestError)
      setError(requestError?.response?.data?.message || 'Unable to load subcontractor jobsites right now.')
    } finally {
      setLoading(false)
    }
  }, [assignmentView, isAdmin, search, sortBy])

  useEffect(() => { loadJobsites() }, [loadJobsites])

  const loadCrews = useCallback(async () => {
    if (!isAdmin) return
    try {
      const response = await axios.get('/api/subcontractor/crews')
      setCrews((response.data?.items || []).filter(c => c.isActive !== false))
    } catch (e) {
      console.error('Failed to load crews for bulk assign', e)
    }
  }, [isAdmin])

  useEffect(() => { loadCrews() }, [loadCrews])

  const visibleOwnedJobsites = useMemo(() => {
    if (assignmentView === 'assigned' || assignmentView === 'overdue') return []
    const queuedJobsiteIds = new Set(items
      .filter(item => item.isSubcontractorOwned && item.scheduleItemId && item.jobsite)
      .map(item => String(item.jobsite)))
    const unqueuedOwnedJobsites = ownedJobsites.filter(item => !queuedJobsiteIds.has(String(item._id)))
    if (assignmentView === 'unmapped') {
      return unqueuedOwnedJobsites.filter(item => !Number.isFinite(item.latitude) || !Number.isFinite(item.longitude))
    }
    return unqueuedOwnedJobsites
  }, [assignmentView, items, ownedJobsites])

  const visibleItems = useMemo(() => ([...items, ...visibleOwnedJobsites]), [items, visibleOwnedJobsites])
  const selectableItems = useMemo(() => visibleItems.filter(item => !item.isSubcontractorOwned || item.scheduleItemId), [visibleItems])
  const visibleSummary = useMemo(() => ({
    ...summary,
    total: Number(summary.total || 0) + visibleOwnedJobsites.length,
    mapped: Number(summary.mapped || 0) + visibleOwnedJobsites.filter(item => Number.isFinite(item.latitude) && Number.isFinite(item.longitude)).length
  }), [summary, visibleOwnedJobsites])

  // Map setup — re-run when viewMode changes so the map initializes
  // after the container div is rendered
  useEffect(() => {
    if (viewMode !== 'map' || !mapContainerRef.current || mapRef.current) return
    const map = L.map(mapContainerRef.current, { center: DEFAULT_CENTER, zoom: 11, zoomControl: true })
    L.tileLayer('https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', {
      attribution: '&copy; Google Maps', maxZoom: 20, minZoom: 0
    }).addTo(map)
    mapRef.current = map
    window.requestAnimationFrame(() => map.invalidateSize())
    return () => { map.remove(); mapRef.current = null; markersRef.current = [] }
  }, [viewMode])

  useEffect(() => {
    if (!mapRef.current || viewMode !== 'map') return
    markersRef.current.forEach(marker => marker.remove())
    markersRef.current = []
    const mappedItems = visibleItems.filter(item => Number.isFinite(item.latitude) && Number.isFinite(item.longitude))
    const bounds = []
    mappedItems.forEach((item) => {
      const marker = L.marker([item.latitude, item.longitude], { icon: buildMarkerIcon(item.isAssigned) })
      marker.bindPopup(`<div style="min-width:220px"><strong>${item.siteName || 'Unnamed Site'}</strong><br/>${item.address || 'No address'}<br/>${item.isSubcontractorOwned ? 'Subcontractor-owned' : (item.assignedCrewName || 'Unassigned')}</div>`)
      marker.addTo(mapRef.current)
      markersRef.current.push(marker)
      bounds.push([item.latitude, item.longitude])
    })
    window.requestAnimationFrame(() => {
      if (!mapRef.current) return
      mapRef.current.invalidateSize()
      if (bounds.length) mapRef.current.fitBounds(bounds, { padding: [40, 40] })
      else mapRef.current.setView(DEFAULT_CENTER, 11)
    })
  }, [visibleItems, viewMode])

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === selectableItems.length) setSelectedIds(new Set())
    else setSelectedIds(new Set(selectableItems.map(i => i._id)))
  }

  const handleBulkAssign = async () => {
    if (!bulkCrewId || !selectedIds.size) return
    setBulkAssigning(true)
    try {
      await Promise.all([...selectedIds].map(id =>
        axios.post('/api/subcontractor/schedule/assign-crew', { scheduleItemId: id, crewId: bulkCrewId })
      ))
      notification?.({ type: 'success', title: 'Bulk assign complete', message: `Assigned ${selectedIds.size} sites.` })
      setSelectedIds(new Set())
      setBulkCrewId('')
      await loadJobsites()
    } catch (e) {
      notification?.({ type: 'danger', title: 'Bulk assign failed', message: e?.response?.data?.message || 'Some assignments may have failed.' })
    } finally {
      setBulkAssigning(false)
    }
  }

  const openOwnedJobsiteDialog = (jobsite = null) => {
    setEditingOwnedJobsite(jobsite)
    setOwnedJobsiteForm(hydrateOwnedJobsiteForm(jobsite))
    setOwnedJobsiteError('')
    setOwnedJobsiteContacts([])
    setOwnedContactForm(EMPTY_OWNED_CONTACT_FORM)
    setOwnedContactError('')
    setOwnedJobsiteDialogOpen(true)
    if (jobsite?._id) loadOwnedJobsiteContacts(jobsite._id)
  }

  const closeOwnedJobsiteDialog = () => {
    if (ownedJobsiteSaving) return
    setOwnedJobsiteDialogOpen(false)
    setEditingOwnedJobsite(null)
    setOwnedJobsiteForm(EMPTY_OWNED_JOBSITE_FORM)
    setOwnedJobsiteError('')
    setOwnedJobsiteContacts([])
    setOwnedContactForm(EMPTY_OWNED_CONTACT_FORM)
    setOwnedContactError('')
  }

  const loadOwnedJobsiteContacts = async (jobsiteId) => {
    if (!jobsiteId) return
    setOwnedJobsiteContactsLoading(true)
    try {
      const response = await axios.get(`/api/subcontractor/owned-jobsites/${jobsiteId}/contacts`)
      setOwnedJobsiteContacts(response.data?.items || [])
    } catch (requestError) {
      console.error('Failed to load subcontractor-owned contacts', requestError)
      setOwnedContactError(requestError?.response?.data?.message || 'Unable to load contacts right now.')
    } finally {
      setOwnedJobsiteContactsLoading(false)
    }
  }

  const saveOwnedContact = async () => {
    if (!editingOwnedJobsite?._id) return
    if (!ownedContactForm.firstName.trim() && !ownedContactForm.lastName.trim()) {
      setOwnedContactError('Contact name is required.')
      return
    }
    if (!ownedContactForm.phone.trim() && !ownedContactForm.email.trim()) {
      setOwnedContactError('Add an email address or phone number.')
      return
    }

    setOwnedContactSaving(true)
    setOwnedContactError('')
    try {
      const response = await axios.post(
        `/api/subcontractor/owned-jobsites/${editingOwnedJobsite._id}/contacts`,
        {
          firstName: ownedContactForm.firstName.trim(),
          lastName: ownedContactForm.lastName.trim(),
          phone: ownedContactForm.phone.trim(),
          email: ownedContactForm.email.trim(),
          notes: ownedContactForm.notes.trim()
        }
      )
      const contact = response.data?.item
      if (contact) setOwnedJobsiteContacts(current => [...current, contact])
      setOwnedContactForm(EMPTY_OWNED_CONTACT_FORM)
      await loadJobsites()
    } catch (requestError) {
      console.error('Failed to save subcontractor-owned contact', requestError)
      setOwnedContactError(requestError?.response?.data?.message || 'Unable to save contact right now.')
    } finally {
      setOwnedContactSaving(false)
    }
  }

  const enqueueOwnedJobsite = async (jobsite) => {
    if (!jobsite?._id || queueingOwnedJobsiteId) return
    setQueueingOwnedJobsiteId(jobsite._id)
    try {
      await axios.post(`/api/subcontractor/owned-jobsites/${jobsite._id}/queue`)
      notification?.({ type: 'success', title: 'Added to herbicide queue', message: `${jobsite.siteName || 'Jobsite'} is ready for crew assignment.` })
      await loadJobsites()
    } catch (requestError) {
      notification?.({ type: 'danger', title: 'Unable to add to queue', message: requestError?.response?.data?.message || 'Please try again.' })
    } finally {
      setQueueingOwnedJobsiteId(null)
    }
  }

  const openSprayZonesDialog = (jobsite) => {
    setSprayZonesJobsite(jobsite)
  }

  const saveOwnedJobsite = async () => {
    if (!ownedJobsiteForm.siteName.trim() || !ownedJobsiteForm.address.trim()) {
      setOwnedJobsiteError('Jobsite name and street address are required.')
      return
    }

    setOwnedJobsiteSaving(true)
    setOwnedJobsiteError('')
    try {
      const payload = {
        ...ownedJobsiteForm,
        siteName: ownedJobsiteForm.siteName.trim(),
        address: ownedJobsiteForm.address.trim(),
        city: ownedJobsiteForm.city.trim(),
        state: ownedJobsiteForm.state.trim(),
        zip: ownedJobsiteForm.zip.trim(),
        notes: ownedJobsiteForm.notes.trim()
      }
      if (editingOwnedJobsite?._id) {
        await axios.put(`/api/subcontractor/owned-jobsites/${editingOwnedJobsite._id}`, payload)
      } else {
        await axios.post('/api/subcontractor/owned-jobsites', payload)
      }
      setOwnedJobsiteDialogOpen(false)
      setEditingOwnedJobsite(null)
      setOwnedJobsiteForm(EMPTY_OWNED_JOBSITE_FORM)
      setOwnedJobsiteError('')
      await loadJobsites()
    } catch (requestError) {
      console.error('Failed to save subcontractor-owned jobsite', requestError)
      setOwnedJobsiteError(requestError?.response?.data?.message || 'Unable to save this jobsite right now.')
    } finally {
      setOwnedJobsiteSaving(false)
    }
  }

  const unassignedCount = visibleSummary.unassigned || 0
  const overdueCount = visibleSummary.overdue || 0

  const summaryCards = useMemo(() => ([
    { eyebrow: 'Visible sites', value: visibleSummary.total || 0, caption: 'Queue sites plus subcontractor-owned jobsites' },
    { eyebrow: 'Mapped', value: `${visibleSummary.mapped || 0}`, caption: `${(visibleSummary.total || 0) - (visibleSummary.mapped || 0)} missing coordinates` },
    { eyebrow: 'Unassigned', value: unassignedCount, caption: unassignedCount > 0 ? 'Queue sites needing a crew' : 'Every queue site has a crew', alert: unassignedCount > 0 },
    { eyebrow: 'Overdue', value: overdueCount, caption: 'Past target application date', alert: overdueCount > 0 }
  ]), [visibleSummary, unassignedCount, overdueCount])

  if (loading && !items.length) {
    return <PortalLoadingCard title="Loading jobsites" message="Loading your properties..." minHeight={280} />
  }

  if (error && !items.length) {
    return <PortalStateCard title="Jobsites unavailable" message={error} actionLabel="Retry" onAction={loadJobsites} tone="danger" minHeight={240} />
  }

  return (
    <div>
      {/* Stats */}
      <Grid container spacing={2}>
        {summaryCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.eyebrow}>
            <PortalMetricCard eyebrow={card.eyebrow} value={card.value} caption={card.caption} borderColor={card.alert ? 'var(--df-warn-text, #8c5b00)' : undefined} />
          </Grid>
        ))}
      </Grid>

      {/* Filter bar */}
      <Box mt={2}>
        <div className={classes.filterBar}>
          <div style={{ flex: 1 }}>
            <TextField
              fullWidth
              label="Search"
              placeholder="Address, property name, or community"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              variant="outlined"
            />
          </div>
          <div>
            <TextField
              select
              label="Sort"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              variant="outlined"
              style={{ minWidth: 180 }}
            >
              {sortOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </div>
          <div>
            <TextField
              select
              label="Show"
              value={assignmentView}
              onChange={(e) => setAssignmentView(e.target.value)}
              variant="outlined"
              style={{ minWidth: 180 }}
            >
              {assignmentViewOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </div>
          <div style={{ flex: 1 }} />
          {isAdmin ? (
            <Button color="primary" variant="contained" onClick={() => openOwnedJobsiteDialog()}>
              Add Jobsite
            </Button>
          ) : null}
          <div style={{ display: 'inline-flex', background: 'var(--df-bg-2)', border: '1px solid var(--df-line)', borderRadius: 10, padding: 3, height: 34, alignItems: 'center' }}>
            <button type="button" onClick={() => setViewMode('list')} style={{ height: 26, padding: '0 14px', border: 'none', background: viewMode === 'list' ? 'var(--df-surface)' : 'transparent', color: viewMode === 'list' ? 'var(--df-ink)' : 'var(--df-ink-3)', fontSize: 12, fontWeight: 600, borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit', boxShadow: viewMode === 'list' ? '0 1px 2px rgba(28,36,25,.06)' : 'none' }}>
              List
            </button>
            <button type="button" onClick={() => setViewMode('map')} style={{ height: 26, padding: '0 14px', border: 'none', background: viewMode === 'map' ? 'var(--df-surface)' : 'transparent', color: viewMode === 'map' ? 'var(--df-ink)' : 'var(--df-ink-3)', fontSize: 12, fontWeight: 600, borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit', boxShadow: viewMode === 'map' ? '0 1px 2px rgba(28,36,25,.06)' : 'none' }}>
              Map
            </button>
          </div>
        </div>
      </Box>

      {viewMode === 'map' ? (
        <Card className={classes.mapCard}>
          <div ref={mapContainerRef} className={classes.mapContainer} />
        </Card>
      ) : (
        <>
          {/* Select all */}
          {isAdmin && selectableItems.length > 0 && (
            <div className={classes.selectAllRow}>
              <Checkbox
                size="small"
                color="primary"
                checked={selectedIds.size === selectableItems.length && selectableItems.length > 0}
                indeterminate={selectedIds.size > 0 && selectedIds.size < selectableItems.length}
                onChange={toggleSelectAll}
                style={{ padding: 0 }}
              />
              <span>Select all on page</span>
              <span style={{ color: 'var(--df-ink-4)' }}>·</span>
              <span>Showing {visibleItems.length} of {visibleSummary.total || visibleItems.length}</span>
            </div>
          )}

          {/* Card grid */}
          <Grid container spacing={2}>
            {visibleItems.map((item) => {
              const isSelected = selectedIds.has(item._id)
              const urgencyChip = getUrgencyChip(item)
              const assignmentChip = getAssignmentChip(item)
              const prompt = getCardPrompt(item, isAdmin)
              const isMapped = Number.isFinite(item.latitude) && Number.isFinite(item.longitude)
              const isUnqueuedOwnedJobsite = item.isSubcontractorOwned && !item.scheduleItemId
              const canSelect = !isUnqueuedOwnedJobsite

              return (
                <Grid item xs={12} md={6} lg={4} key={item._id}>
                  <Card
                    className={`${classes.siteCard} ${isSelected ? classes.siteCardSelected : ''}`}
                    elevation={0}
                    onClick={() => {
                      if (isUnqueuedOwnedJobsite) {
                        if (isAdmin) openOwnedJobsiteDialog(item)
                        return
                      }
                      setSelectedScheduleItemId(item.scheduleItemId || item._id)
                    }}
                  >
                    {/* Urgency left rule */}
                    <div className={classes.urgencyRule} style={{ background: getUrgencyColor(item.status) }} />

                    <CardContent style={{ paddingLeft: 18 }}>
                      {/* Top row: checkbox + title + map pin */}
                      <div className={classes.cardTop}>
                        {isAdmin && canSelect && (
                          <Checkbox
                            size="small"
                            color="primary"
                            checked={isSelected}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleSelect(item._id)}
                            style={{ padding: 0, marginTop: 3 }}
                          />
                        )}
                        <div className={classes.cardTitleBlock}>
                          <div className={classes.siteName}>
                            {item.siteName || 'Unnamed Site'}
                          </div>
                          <div className={classes.siteAddress}>
                            {item.address || 'No address'}
                          </div>
                        </div>
                        <div className={`${classes.mapPin} ${!isMapped ? classes.mapPinUnmapped : ''}`} title={isMapped ? 'Mapped' : 'Unmapped'}>
                          {isMapped ? '●' : '⚠'}
                        </div>
                      </div>

                      {/* Chips: max 2 — urgency + assignment */}
                      <div className={classes.chipRow}>
                        {urgencyChip && <Chip size="small" label={urgencyChip.label} className={urgencyChip.className} />}
                        <Chip size="small" label={assignmentChip.label} className={assignmentChip.className} />
                      </div>

                      {/* Meta footer */}
                      <div className={classes.cardMeta}>
                        <div>
                          <span className={classes.metaLabel}>Last</span>
                          <span className={item.lastAppliedDate ? classes.metaValue : classes.metaValueDim}>
                            {formatPortalDate(item.lastAppliedDate, { fallback: 'No prior application' })}
                          </span>
                        </div>
                        <button
                          type="button"
                          className={classes.cardPrompt}
                          onClick={(e) => {
                            e.stopPropagation()
                            if (isUnqueuedOwnedJobsite) {
                              if (isAdmin) enqueueOwnedJobsite(item)
                              return
                            }
                            if (item.isSubcontractorOwned && isAdmin) {
                              openSprayZonesDialog(item)
                              return
                            }
                            setSelectedScheduleItemId(item.scheduleItemId || item._id)
                          }}
                          disabled={isUnqueuedOwnedJobsite && (!isAdmin || queueingOwnedJobsiteId === item._id)}
                        >
                          {isUnqueuedOwnedJobsite && queueingOwnedJobsiteId === item._id ? 'Adding to queue…' : prompt}
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
          </Grid>

          {!visibleItems.length && (
            <PortalStateCard title="No jobsites match filters" message="Try adjusting your search or filter settings." minHeight={200} />
          )}
        </>
      )}

      {/* Bulk action bar */}
      {isAdmin && selectedIds.size > 0 && (
        <div className={classes.bulkBar}>
          <span className={classes.bulkCount}>{selectedIds.size} selected</span>
          <span className={classes.bulkSep} />
          <span className={classes.bulkLabel}>Assign to</span>
          <select
            value={bulkCrewId}
            onChange={(e) => setBulkCrewId(e.target.value)}
            style={{ background: 'rgba(255,255,255,.08)', color: '#f4efe4', border: '1px solid rgba(255,255,255,.14)', height: 30, padding: '0 26px 0 10px', borderRadius: 8, fontSize: 12, fontFamily: 'inherit', appearance: 'none', cursor: 'pointer' }}
          >
            <option value="">Choose crew…</option>
            {crews.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
          <button
            type="button"
            onClick={handleBulkAssign}
            disabled={!bulkCrewId || bulkAssigning}
            style={{ height: 30, padding: '0 12px', fontSize: 12, fontWeight: 600, borderRadius: 8, cursor: 'pointer', background: 'var(--df-primary)', color: '#fff', border: '1px solid var(--df-primary-2, #164a2c)', fontFamily: 'inherit' }}
          >
            {bulkAssigning ? 'Assigning…' : 'Assign'}
          </button>
          <span className={classes.bulkSep} />
          <button
            type="button"
            onClick={() => { setSelectedIds(new Set()); setBulkCrewId('') }}
            style={{ background: 'none', border: 'none', color: '#a8b8a4', fontSize: 16, cursor: 'pointer', padding: 4, fontFamily: 'inherit' }}
            title="Clear selection"
          >
            ×
          </button>
        </div>
      )}

      <SubcontractorSiteDetailDrawer
        scheduleItemId={selectedScheduleItemId}
        open={Boolean(selectedScheduleItemId)}
        onClose={() => setSelectedScheduleItemId(null)}
      />

      <Dialog open={ownedJobsiteDialogOpen} onClose={closeOwnedJobsiteDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingOwnedJobsite ? 'Edit subcontractor jobsite' : 'Add subcontractor jobsite'}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" className={classes.metaValueDim} style={{ marginBottom: 16 }}>
            This jobsite belongs only to your company. It is not sent to Aspire and is separate from the office queue.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Jobsite name"
                value={ownedJobsiteForm.siteName}
                onChange={(event) => setOwnedJobsiteForm(current => ({ ...current, siteName: event.target.value }))}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Street address"
                value={ownedJobsiteForm.address}
                onChange={(event) => setOwnedJobsiteForm(current => ({ ...current, address: event.target.value }))}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="City"
                value={ownedJobsiteForm.city}
                onChange={(event) => setOwnedJobsiteForm(current => ({ ...current, city: event.target.value }))}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="State"
                value={ownedJobsiteForm.state}
                onChange={(event) => setOwnedJobsiteForm(current => ({ ...current, state: event.target.value }))}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                label="ZIP"
                value={ownedJobsiteForm.zip}
                onChange={(event) => setOwnedJobsiteForm(current => ({ ...current, zip: event.target.value }))}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Latitude (optional)"
                type="number"
                value={ownedJobsiteForm.latitude}
                onChange={(event) => setOwnedJobsiteForm(current => ({ ...current, latitude: event.target.value }))}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Longitude (optional)"
                type="number"
                value={ownedJobsiteForm.longitude}
                onChange={(event) => setOwnedJobsiteForm(current => ({ ...current, longitude: event.target.value }))}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Site notes (optional)"
                value={ownedJobsiteForm.notes}
                onChange={(event) => setOwnedJobsiteForm(current => ({ ...current, notes: event.target.value }))}
                variant="outlined"
              />
            </Grid>
          </Grid>
          {editingOwnedJobsite?._id ? (
            <Box mt={3} pt={2} style={{ borderTop: '1px solid var(--df-line)' }}>
              <Typography variant="subtitle2" style={{ marginBottom: 4 }}>Jobsite contacts</Typography>
              <Typography variant="body2" className={classes.metaValueDim} style={{ marginBottom: 12 }}>
                Contacts stay with this subcontractor-only jobsite and are included with its herbicide packet.
              </Typography>
              {ownedJobsiteContactsLoading ? (
                <Typography variant="body2" className={classes.metaValueDim}>Loading contacts…</Typography>
              ) : ownedJobsiteContacts.length ? (
                <Box mb={2}>
                  {ownedJobsiteContacts.map(contact => (
                    <Typography key={contact._id} variant="body2" style={{ marginBottom: 4 }}>
                      {contact.name}{contact.phone ? ` · ${contact.phone}` : ''}{contact.email ? ` · ${contact.email}` : ''}
                    </Typography>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" className={classes.metaValueDim} style={{ marginBottom: 12 }}>
                  No contacts yet.
                </Typography>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First name"
                    value={ownedContactForm.firstName}
                    onChange={(event) => setOwnedContactForm(current => ({ ...current, firstName: event.target.value }))}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last name"
                    value={ownedContactForm.lastName}
                    onChange={(event) => setOwnedContactForm(current => ({ ...current, lastName: event.target.value }))}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={ownedContactForm.phone}
                    onChange={(event) => setOwnedContactForm(current => ({ ...current, phone: event.target.value }))}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={ownedContactForm.email}
                    onChange={(event) => setOwnedContactForm(current => ({ ...current, email: event.target.value }))}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button color="primary" onClick={saveOwnedContact} disabled={ownedContactSaving}>
                    {ownedContactSaving ? 'Adding contact…' : 'Add contact'}
                  </Button>
                </Grid>
              </Grid>
              {ownedContactError ? (
                <Typography variant="body2" color="error" style={{ marginTop: 10 }}>
                  {ownedContactError}
                </Typography>
              ) : null}
            </Box>
          ) : null}
          {ownedJobsiteError ? (
            <Typography variant="body2" color="error" style={{ marginTop: 14 }}>
              {ownedJobsiteError}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeOwnedJobsiteDialog} disabled={ownedJobsiteSaving}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={saveOwnedJobsite} disabled={ownedJobsiteSaving}>
            {ownedJobsiteSaving ? 'Saving…' : (editingOwnedJobsite ? 'Save changes' : 'Add jobsite')}
          </Button>
        </DialogActions>
      </Dialog>

      <SubcontractorSprayZonesDialog
        open={Boolean(sprayZonesJobsite)}
        jobsiteId={sprayZonesJobsite?.jobsite || sprayZonesJobsite?._id}
        jobsite={sprayZonesJobsite}
        onClose={() => setSprayZonesJobsite(null)}
        onSaved={loadJobsites}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  user: state.user,
  notification: state.notification
})

export default connect(mapStateToProps)(SubcontractorJobsites)
